/**
 * Document and step requirements for Sumsub verification.
 * Used by the pre-step explainer and the side reference panel.
 * - Individuals: penning-kyc level (KYC)
 * - Companies: penning-kyb level (KYB)
 */

export interface RequirementSection {
  title: string
  items: string[]
}

/** Full requirements for the pre-step explainer and helper (KYC – penning-kyc). */
export const KYC_REQUIREMENTS: RequirementSection[] = [
  {
    title: 'Identity document',
    items: [
      'A valid passport, driver’s license, or national ID card. Residence permits are not accepted.',
      'All four corners must be visible; no cropping.',
      'Document must be clearly visible: no glare, blur, or obstructions. Text and photo must be readable.',
      'Full name, date of birth, document number, issue date, and photo must be present and legible.',
      'Color only; not edited, converted to PDF, or altered.',
      'Min 100 KB file size or 300 DPI. Formats: JPG, JPEG, PNG, PDF, HEIC.',
    ],
  },
  {
    title: 'Selfie / liveness',
    items: [
      'You’ll complete a short selfie or liveness check (e.g. advanced liveness or short video with digits).',
      'Face the camera in good light; ensure your face is clearly visible.',
    ],
  },
  {
    title: 'Personal data',
    items: [
      'Name, date of birth, and other details as requested in the flow. Enter exactly as shown on your identity document.',
    ],
  },
]

/** Full requirements for the pre-step explainer and helper (KYB – penning-kyb). */
export const KYB_REQUIREMENTS: RequirementSection[] = [
  {
    title: 'Company details',
    items: [
      'Company name, registration number, legal form, address, date of incorporation. Must match official records.',
    ],
  },
  {
    title: 'Company documents',
    items: [
      'E.g. certificate of incorporation/registration, articles of association, memorandum/bylaws, shareholder/director registry, certificate of incumbency (or similar).',
      'Issued within 12 months where applicable (or most recent from registry).',
      'Documents must be clearly visible: full text readable, no cut-off edges. OCR-quality scan or photo; screenshots are not accepted.',
      'Signed/stamped by state register, registered agent, notary, or company director/secretary with company seal where required.',
    ],
  },
  {
    title: 'Ownership and control',
    items: [
      'Documents establishing beneficial ownership and control (e.g. shareholder registry, UBO details, certificate of incumbency) as requested. Names and roles must be clearly legible.',
    ],
  },
]

/** Short recap for the side panel (KYC). */
export const KYC_REQUIREMENTS_RECAP: RequirementSection[] = [
  {
    title: 'Identity document',
    items: ['Passport, driver’s license, or national ID; color; min 100 KB.'],
  },
  { title: 'Selfie / liveness', items: ['Short selfie or video check.'] },
  {
    title: 'Personal data',
    items: ['Name, DOB, and other requested details.'],
  },
]

/** Short recap for the side panel (KYB). */
export const KYB_REQUIREMENTS_RECAP: RequirementSection[] = [
  {
    title: 'Company details',
    items: [
      'Name, registration number, legal form, address, incorporation date.',
    ],
  },
  {
    title: 'Company documents',
    items: [
      'Certificate of incorporation, articles, shareholder/director registry; within 12 months; scan or photo.',
    ],
  },
  { title: 'Ownership', items: ['Beneficial ownership / control structure.'] },
]

/** Map Sumsub idDocSetType (or synthetic step ids) to friendly step labels. */
export const STEP_LABEL_MAP: Record<string, string> = {
  IDENTITY: 'Identity document',
  IDENTITY2: 'Identity document (2)',
  IDENTITY3: 'Identity document (3)',
  IDENTITY4: 'Identity document (4)',
  APPLICANT_DATA: 'Personal details',
  LIVENESS: 'Selfie & liveness',
  COMPANY: 'Company verification',
  COMPANY_DATA: 'Company details',
  COMPANY_DOCUMENTS: 'Company documents',
  COMPANY_BENEFICIARIES: 'Beneficial owners',
}

export function getStepLabel(idDocSetType: string): string {
  return STEP_LABEL_MAP[idDocSetType] ?? idDocSetType ?? 'Verification step'
}
